import { Fragment, useEffect, useMemo, useState } from "react";
import { api, fmtEur, fmtDate, formatApiError, downloadFile, downloadScheda } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FileDown, FileSpreadsheet, FileText, UserPlus, X, Zap, Check, Boxes, Settings2, Archive, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { CorsistaFields } from "../components/CorsistaFields";

const STATO = {
  pagato: { label: "Pagato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  non_pagato: { label: "Non Pagato", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_sospeso: { label: "In Sospeso", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export function StatoBadge({ stato }) {
  const s = STATO[stato] || STATO.in_sospeso;
  return <Badge className={`${s.cls} border hover:opacity-100`} data-testid={`stato-${stato}`}>{s.label}</Badge>;
}

const EVASIONE = {
  evaso: { label: "Evaso", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  in_attesa: { label: "In attesa", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  trasmesso: { label: "Trasmesso", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

export function EvasioneBadge({ stato }) {
  const s = EVASIONE[stato] || EVASIONE.in_attesa;
  return <Badge className={`${s.cls} border hover:opacity-100`} data-testid={`evasione-${stato}`}>{s.label}</Badge>;
}

const MODALITA = { bonifico: "Bonifico", contanti: "Contanti", carta: "Carta", omaggio: "Omaggio", fondi: "Fondi", abbonamento: "Abbonamento" };
const INCASSO = { safe: "Safe", partner: "Partner", ics: "Associazione I.C.S." };
const AVANZAMENTO = {
  in_completamento: { label: "In completamento", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  attivato: { label: "Attivato", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  trasmesso: { label: "Trasmesso", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  completato: { label: "Completato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const FAD = { lcr: "LCR", kattedra: "Kattedra", safe: "Safe" };

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_SALE_CORSISTA = { nome: "", cognome: "", codice_fiscale: "", email: "", telefono: "", cellulare: "", data_nascita: "", luogo_nascita: "", provincia_nascita: "", sesso: "", indirizzo: "", citta: "", cap: "", mansione: "" };
const EMPTY_LINE = { servizio_id: "", prezzo_vendita: "", costo_servizio: "", quantita: 1, iva_rate: 0.22, fad: "none", costo_piattaforma: "", stato_avanzamento: "in_completamento", corsisti: [] };
const makeEmpty = () => ({
  data: today(), azienda_id: "", partner_id: "", stato_pagamento: "in_sospeso", modalita_incasso: "bonifico",
  incassato_da: "ics", numero_fattura: "", numero_ordine: "", stato_evasione: "in_attesa", note: "", righe: [{ ...EMPTY_LINE }],
});

export default function Sales() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const [sales, setSales] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ dal: "", al: "", partner_id: "", azienda_id: "", servizio_id: "", stato: "", saldato: "" });
  const [nameSearch, setNameSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(makeEmpty());
  const [editId, setEditId] = useState(null);
  const emptyQuick = () => ({ corsista: { ...EMPTY_SALE_CORSISTA }, servizi: [], search: "" });
  const [quick, setQuick] = useState(emptyQuick());
  const [packages, setPackages] = useState([]);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgForm, setPkgForm] = useState({ nome: "", servizio_ids: [], search: "" });
  const [pkgEditId, setPkgEditId] = useState(null);

  const buildParams = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  };

  const load = () => api.get("/sales", { params: buildParams() }).then((r) => setSales(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const [settle, setSettle] = useState(null);
  const loadSettle = () => {
    if (!isOwner) return;
    const p = {};
    if (filters.dal) p.dal = filters.dal;
    if (filters.al) p.al = filters.al;
    if (filters.saldato) p.saldato = filters.saldato;
    api.get("/sales/settlement", { params: p }).then((r) => setSettle(r.data)).catch(() => setSettle(null));
  };
  useEffect(() => { loadSettle(); /* eslint-disable-next-line */ }, [filters.dal, filters.al, filters.saldato, sales]);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/services").then((r) => setServices(r.data));
    if (isOwner) api.get("/partners").then((r) => setPartners(r.data));
    if (isOwner) api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, [isOwner]);
  const loadPackages = () => api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});

  const currentPartner = useMemo(() => partners.find((p) => p.id === form.partner_id), [partners, form.partner_id]);
  const isRivenditore = currentPartner?.tipo_partner === "rivenditore";

  const displaySales = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    if (!q) return sales;
    return sales.filter((s) => {
      const base = `${s.azienda_nome || ""} ${s.partner_nome || ""} ${s.numero_fattura || ""} ${s.numero_ordine || ""}`;
      const righe = (s.righe || []).map((r) => `${r.servizio_nome || ""} ${(r.corsisti || []).map((c) => `${c.nome || ""} ${c.cognome || ""} ${c.codice_fiscale || ""}`).join(" ")}`).join(" ");
      return `${base} ${righe}`.toLowerCase().includes(q);
    });
  }, [sales, nameSearch]);
  const royaltyPct = (currentPartner && !isRivenditore) ? Number(currentPartner.royalty_percent) : 0;
  const rivPct = isRivenditore ? Number(currentPartner.rivendita_percent ?? 50) : 0;
  // Prezzo riservato al rivenditore: campo dedicato se impostato, altrimenti % rivendita sul listino
  const priceForSale = (svc) => {
    if (!svc) return "";
    if (!isRivenditore) return svc.prezzo_vendita;
    if (Number(svc.prezzo_rivenditore) > 0) return svc.prezzo_rivenditore;
    return Math.round((Number(svc.prezzo_vendita) || 0) * rivPct) / 100;
  };
  const totals = useMemo(() => {
    let prezzo = 0, costo = 0, iva = 0, royalty = 0, rivendita = 0;
    form.righe.forEach((r) => {
      const qta = parseFloat(r.quantita) || 1;
      const cp = parseFloat(r.costo_piattaforma) || 0;
      const p = ((parseFloat(r.prezzo_vendita) || 0) + cp) * qta;
      const c = ((parseFloat(r.costo_servizio) || 0) + cp) * qta;
      prezzo += p; costo += c;
      iva += p * Number(r.iva_rate || 0);
      royalty += (p - c) * royaltyPct / 100;
      if (isRivenditore) rivendita += (p * rivPct / 100) * (1 + Number(r.iva_rate || 0));
    });
    return { prezzo, costo, iva, royalty, rivendita, utile: prezzo - costo - royalty - iva };
  }, [form.righe, royaltyPct, isRivenditore, rivPct]);

  const addLine = () => setForm((f) => ({ ...f, righe: [...f.righe, { ...EMPTY_LINE, corsisti: [] }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, righe: f.righe.filter((_, idx) => idx !== i) }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, righe: f.righe.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addCorsista = (i) => setForm((f) => ({ ...f, righe: f.righe.map((r, idx) => (idx === i ? { ...r, corsisti: [...(r.corsisti || []), { ...EMPTY_SALE_CORSISTA }] } : r)) }));
  const removeCorsista = (i, ci) => setForm((f) => ({ ...f, righe: f.righe.map((r, idx) => (idx === i ? { ...r, corsisti: r.corsisti.filter((_, k) => k !== ci) } : r)) }));
  const setCorsista = (i, ci, obj) => setForm((f) => ({ ...f, righe: f.righe.map((r, idx) => (idx === i ? { ...r, corsisti: r.corsisti.map((c, k) => (k === ci ? obj : c)) } : r)) }));
  const onLineService = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    const prezzo = svc ? priceForSale(svc) : "";
    setLine(i, {
      servizio_id: sid,
      prezzo_vendita: prezzo,
      costo_servizio: svc ? svc.costo_servizio : "",
      iva_rate: svc ? (svc.regime_iva === "esente" ? 0 : 0.22) : 0.22,
    });
  };

  const lineFromService = (sid) => {
    const svc = services.find((s) => s.id === sid);
    const prezzo = svc ? priceForSale(svc) : "";
    return {
      ...EMPTY_LINE,
      servizio_id: sid,
      prezzo_vendita: prezzo,
      costo_servizio: svc ? svc.costo_servizio : "",
      iva_rate: svc ? (svc.regime_iva === "esente" ? 0 : 0.22) : 0.22,
      corsisti: [],
    };
  };

  const toggleQuickServizio = (sid) => setQuick((q) => ({
    ...q, servizi: q.servizi.includes(sid) ? q.servizi.filter((x) => x !== sid) : [...q.servizi, sid],
  }));

  const quickAdd = () => {
    const c = quick.corsista;
    if (!c.nome && !c.cognome) return toast.error("Inserisci almeno nome e cognome del corsista");
    if (quick.servizi.length === 0) return toast.error("Seleziona almeno un corso");
    setForm((f) => {
      let righe = f.righe.filter((r) => r.servizio_id || (r.corsisti && r.corsisti.length));
      quick.servizi.forEach((sid) => {
        const idx = righe.findIndex((r) => r.servizio_id === sid);
        if (idx === -1) {
          const nl = lineFromService(sid);
          nl.corsisti = [{ ...c }];
          righe = [...righe, nl];
        } else {
          righe = righe.map((r, k) => (k === idx ? { ...r, corsisti: [...(r.corsisti || []), { ...c }] } : r));
        }
      });
      if (righe.length === 0) righe = [{ ...EMPTY_LINE }];
      return { ...f, righe };
    });
    toast.success(`Corsista aggiunto a ${quick.servizi.length} cors${quick.servizi.length === 1 ? "o" : "i"}`);
    setQuick(emptyQuick());
  };

  const applyPackage = (p) => {
    const ids = (p.servizio_ids || []).filter((id) => services.some((s) => s.id === id));
    setQuick((q) => ({ ...q, servizi: Array.from(new Set([...q.servizi, ...ids])) }));
    toast.success(`Pacchetto "${p.nome}": ${ids.length} cors${ids.length === 1 ? "o" : "i"} spuntat${ids.length === 1 ? "o" : "i"}`);
  };
  const openPkgManage = () => { setPkgForm({ nome: "", servizio_ids: [], search: "" }); setPkgEditId(null); setPkgOpen(true); };
  const editPkg = (p) => { setPkgForm({ nome: p.nome, servizio_ids: [...(p.servizio_ids || [])], search: "" }); setPkgEditId(p.id); };
  const togglePkgServizio = (sid) => setPkgForm((f) => ({
    ...f, servizio_ids: f.servizio_ids.includes(sid) ? f.servizio_ids.filter((x) => x !== sid) : [...f.servizio_ids, sid],
  }));
  const savePkg = async () => {
    if (!pkgForm.nome.trim()) return toast.error("Indica un nome per il pacchetto");
    if (pkgForm.servizio_ids.length === 0) return toast.error("Seleziona almeno un corso");
    try {
      const payload = { nome: pkgForm.nome.trim(), servizio_ids: pkgForm.servizio_ids };
      if (pkgEditId) await api.put(`/packages/${pkgEditId}`, payload);
      else await api.post("/packages", payload);
      toast.success(pkgEditId ? "Pacchetto aggiornato" : "Pacchetto creato");
      setPkgForm({ nome: "", servizio_ids: [], search: "" }); setPkgEditId(null); loadPackages();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPkg = async (id) => {
    if (!window.confirm("Eliminare questo pacchetto?")) return;
    await api.delete(`/packages/${id}`); toast.success("Pacchetto eliminato");
    if (pkgEditId === id) { setPkgForm({ nome: "", servizio_ids: [], search: "" }); setPkgEditId(null); }
    loadPackages();
  };

  const openNew = () => { setForm(makeEmpty()); setEditId(null); setQuick(emptyQuick()); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      data: s.data, azienda_id: s.azienda_id, partner_id: s.partner_id, stato_pagamento: s.stato_pagamento,
      modalita_incasso: s.modalita_incasso || "bonifico", incassato_da: s.incassato_da || "safe", numero_fattura: s.numero_fattura || "",
      numero_ordine: s.numero_ordine || "", stato_evasione: s.stato_evasione || "in_attesa", note: s.note || "",
      righe: (s.righe || []).map((r) => ({
        servizio_id: r.servizio_id, prezzo_vendita: r.prezzo_vendita, costo_servizio: r.costo_servizio,
        quantita: r.quantita || 1, iva_rate: r.iva_rate, fad: r.fad || "none",
        costo_piattaforma: r.costo_piattaforma || "",
        stato_avanzamento: r.stato_avanzamento || "in_completamento",
        corsisti: (r.corsisti || []).map((c) => ({ ...EMPTY_SALE_CORSISTA, ...c })),
      })),
    });
    setEditId(s.id); setQuick(emptyQuick()); setOpen(true);
  };

  const save = async () => {
    if (!form.azienda_id || !form.partner_id) return toast.error("Seleziona azienda e partner");
    const righe = form.righe.filter((r) => r.servizio_id);
    if (righe.length === 0) return toast.error("Aggiungi almeno un corso/servizio");
    try {
      const payload = {
        data: form.data, azienda_id: form.azienda_id, partner_id: form.partner_id,
        numero_fattura: form.numero_fattura, numero_ordine: form.numero_ordine,
        modalita_incasso: form.modalita_incasso, incassato_da: form.incassato_da, stato_pagamento: form.stato_pagamento,
        stato_evasione: form.stato_evasione, note: form.note,
        righe: righe.map((r) => ({
          servizio_id: r.servizio_id, prezzo_vendita: parseFloat(r.prezzo_vendita) || 0,
          costo_servizio: parseFloat(r.costo_servizio) || 0, quantita: parseFloat(r.quantita) || 1,
          iva_rate: Number(r.iva_rate), fad: r.fad === "none" ? "" : r.fad,
          costo_piattaforma: parseFloat(r.costo_piattaforma) || 0,
          stato_avanzamento: r.stato_avanzamento || "in_completamento",
          corsisti: (r.corsisti || []).filter((c) => c.nome || c.cognome || c.codice_fiscale || c.email),
        })),
      };
      if (editId) await api.put(`/sales/${editId}`, payload);
      else await api.post("/sales", payload);
      toast.success(editId ? "Vendita aggiornata" : "Vendita registrata con successo");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa vendita?")) return;
    await api.delete(`/sales/${id}`); toast.success("Vendita eliminata"); load();
  };

  const schedaSale = async (s, c, corso) => {
    const az = companies.find((x) => x.id === s.azienda_id) || { nome: s.azienda_nome };
    try { await downloadScheda(c, az, corso); }
    catch (e) { toast.error("Impossibile generare la scheda"); }
  };

  const togglePagato = async (s) => {
    const nuovo = s.stato_pagamento === "pagato" ? "in_sospeso" : "pagato";
    try {
      await api.put(`/sales/${s.id}/stato-pagamento`, { stato_pagamento: nuovo });
      toast.success(nuovo === "pagato" ? "Vendita segnata come Incassata" : "Vendita rimessa In Sospeso");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSaldato = async (s) => {
    try {
      await api.put(`/sales/${s.id}/saldato`, { saldato: !s.saldato });
      toast.success(!s.saldato ? "Vendita segnata come SALDATA" : "Vendita segnata come da saldare");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saldaPeriodo = async (partner_id, nome) => {
    const per = filters.dal || filters.al ? "nel periodo selezionato" : "(TUTTE le vendite: nessun periodo selezionato)";
    if (!window.confirm(`Segnare come SALDATE tutte le vendite di ${nome} ${per}?`)) return;
    try {
      const r = await api.post("/sales/salda-periodo", { partner_id, dal: filters.dal || null, al: filters.al || null, saldato: true });
      toast.success(`${r.data.aggiornate} vendite segnate come saldate`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [storico, setStorico] = useState(null);
  const openStorico = () => api.get("/saldi-periodo").then((r) => setStorico(r.data)).catch(() => toast.error("Errore nel caricamento storico"));
  const delStorico = async (rid) => {
    if (!window.confirm("Eliminare questa registrazione dallo storico? (le vendite restano come sono)")) return;
    try { await api.delete(`/saldi-periodo/${rid}`); setStorico((l) => l.filter((x) => x.id !== rid)); toast.success("Registrazione eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportFile = async (fmt) => {
    const params = new URLSearchParams(buildParams()).toString();
    const ext = fmt === "excel" ? "xlsx" : fmt;
    const suffix = filters.saldato === "false" ? "_da_saldare" : "";
    try {
      await downloadFile(`/sales/export/${fmt}${params ? `?${params}` : ""}`, `vendite${suffix}.${ext}`);
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{isOwner ? "Gestione Vendite" : "Le Mie Vendite"}</h1>
          <p className="text-sm text-slate-500 mt-1">Calcolo automatico di IVA, royalty partner e utile netto.</p>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <>
              <Button variant={filters.saldato === "false" ? "default" : "outline"}
                onClick={() => setFilters({ ...filters, saldato: filters.saldato === "false" ? "" : "false" })}
                data-testid="filter-da-saldare"
                className={filters.saldato === "false" ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}>
                Solo da saldare
              </Button>
              <Button variant="outline" onClick={openStorico} data-testid="storico-saldi-btn"><Archive className="h-4 w-4 mr-1.5" /> Storico saldi</Button>
            </>
          )}
          <Button variant="outline" onClick={() => exportFile("csv")} data-testid="export-csv"><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => exportFile("excel")} data-testid="export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button variant="outline" onClick={() => exportFile("pdf")} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
          {isOwner && <Button onClick={openNew} data-testid="add-sale-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white"><Plus className="h-4 w-4 mr-1.5" /> Nuova Vendita</Button>}
        </div>
      </div>

      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="mb-3 relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="Ricerca rapida per nome: azienda, partner, corso, corsista, n. fattura/ordine…"
            data-testid="sales-name-search"
            className="h-9 pl-9 pr-9"
          />
          {nameSearch && (
            <button onClick={() => setNameSearch("")} data-testid="sales-name-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div><Label className="text-xs text-slate-600 font-medium">Dal</Label><Input type="date" value={filters.dal} onChange={(e) => setFilters({ ...filters, dal: e.target.value })} data-testid="filter-dal" className="mt-1 h-9" /></div>
          <div><Label className="text-xs text-slate-600 font-medium">Al</Label><Input type="date" value={filters.al} onChange={(e) => setFilters({ ...filters, al: e.target.value })} data-testid="filter-al" className="mt-1 h-9" /></div>
          <div><Label className="text-xs text-slate-600 font-medium">Servizio/Corso</Label>
            <Select value={filters.servizio_id || "all"} onValueChange={(v) => setFilters({ ...filters, servizio_id: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-servizio"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isOwner && (
            <div><Label className="text-xs text-slate-600 font-medium">Partner</Label>
              <Select value={filters.partner_id || "all"} onValueChange={(v) => setFilters({ ...filters, partner_id: v === "all" ? "" : v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="filter-partner"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs text-slate-600 font-medium">Azienda</Label>
            <Select value={filters.azienda_id || "all"} onValueChange={(v) => setFilters({ ...filters, azienda_id: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-azienda"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-600 font-medium">Pagamento</Label>
            <Select value={filters.stato || "all"} onValueChange={(v) => setFilters({ ...filters, stato: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-stato"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="pagato">Pagato</SelectItem>
                <SelectItem value="non_pagato">Non Pagato</SelectItem>
                <SelectItem value="in_sospeso">In Sospeso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                <th className="py-3 px-4">Data</th>
                <th className="py-3 px-4">N. Fattura</th>
                <th className="py-3 px-4">N. Ordine</th>
                <th className="py-3 px-4">Azienda</th>
                <th className="py-3 px-4">Servizio/Corso</th>
                <th className="py-3 px-4">FAD</th>
                <th className="py-3 px-4 text-right">Q.tà</th>
                {isOwner && <th className="py-3 px-4">Partner</th>}
                <th className="py-3 px-4 text-right">Prezzo</th>
                <th className="py-3 px-4 text-right">IVA</th>
                <th className="py-3 px-4 text-right">Royalty</th>
                <th className="py-3 px-4 text-right">Costi</th>
                <th className="py-3 px-4 text-right">Utile Netto</th>
                <th className="py-3 px-4">Incasso</th>
                <th className="py-3 px-4">Inc. da</th>
                <th className="py-3 px-4">Pagamento</th>
                <th className="py-3 px-4">Evasione</th>
                {isOwner && <th className="py-3 px-4"></th>}
              </tr>
            </thead>
            <tbody>
              {displaySales.map((s) => (
                <Fragment key={s.id}>
                  <tr className="border-t-2 border-slate-200 bg-slate-50/70" data-testid={`sale-row-${s.id}`}>
                    <td className="py-3 px-4 text-slate-700 whitespace-nowrap font-medium">{fmtDate(s.data)}</td>
                    <td className="py-3 px-4 text-slate-600">{s.numero_fattura || "—"}</td>
                    <td className="py-3 px-4 text-slate-600">{s.numero_ordine || "—"}</td>
                    <td className="py-3 px-4 font-semibold text-slate-800">{s.azienda_nome}</td>
                    <td className="py-3 px-4 text-slate-400 italic whitespace-nowrap">{s.num_righe} {s.num_righe === 1 ? "corso" : "corsi"}</td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4"></td>
                    {isOwner && <td className="py-3 px-4 text-slate-600">{s.partner_nome}</td>}
                    <td className="py-3 px-4 text-right font-semibold text-slate-800">{fmtEur(s.prezzo_totale)}</td>
                    <td className="py-3 px-4 text-right text-slate-500">{fmtEur(s.iva_totale)}</td>
                    <td className="py-3 px-4 text-right text-amber-700 font-medium">{fmtEur(s.royalty_totale)}</td>
                    <td className="py-3 px-4 text-right text-slate-500">{fmtEur(s.costo_totale)}</td>
                    <td className="py-3 px-4 text-right font-bold text-emerald-700">{fmtEur(s.utile_totale)}</td>
                    <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{MODALITA[s.modalita_incasso] || "—"}</td>
                    <td className="py-3 px-4"><span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${s.incassato_da === "partner" ? "bg-amber-50 text-amber-700 border-amber-200" : s.incassato_da === "ics" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-dusty-50 text-dusty-700 border-dusty-200"}`}>{INCASSO[s.incassato_da] || "Safe"}</span></td>
                    <td className="py-3 px-4">
                      {isOwner ? (
                        <button onClick={() => togglePagato(s)} title="Clicca per cambiare stato incasso" data-testid={`toggle-pagato-${s.id}`} className="cursor-pointer hover:opacity-80 transition-opacity">
                          <StatoBadge stato={s.stato_pagamento} />
                        </button>
                      ) : <StatoBadge stato={s.stato_pagamento} />}
                    </td>
                    <td className="py-3 px-4"><EvasioneBadge stato={s.stato_evasione} /></td>
                    {isOwner && (
                      <td className="py-3 px-4">
                        <div className="flex gap-1 justify-end items-center">
                          <button onClick={() => toggleSaldato(s)} data-testid={`toggle-saldato-${s.id}`}
                            title={s.saldato ? `Saldato il ${s.saldato_data || ""}` : "Segna come saldato"}
                            className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${s.saldato ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}>
                            {s.saldato ? "Saldato" : "Da saldare"}
                          </button>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sale-${s.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`delete-sale-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {(s.righe || []).map((r, idx) => (
                    <tr key={`${s.id}-${idx}`} className="border-b border-slate-100 hover:bg-slate-50 text-xs">
                      <td className="px-4"></td><td className="px-4"></td><td className="px-4"></td><td className="px-4"></td>
                      <td className="py-2 px-4 text-slate-600 pl-8">↳ {r.servizio_nome}
                        <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${(AVANZAMENTO[r.stato_avanzamento] || AVANZAMENTO.in_completamento).cls}`} data-testid={`avanzamento-${s.id}-${idx}`}>{(AVANZAMENTO[r.stato_avanzamento] || AVANZAMENTO.in_completamento).label}</span>
                        {(r.corsisti || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid={`sale-corsisti-${s.id}-${idx}`}>
                            {r.corsisti.map((c, ci) => (
                              <button key={ci} type="button" onClick={() => schedaSale(s, c, r.servizio_nome)}
                                className="inline-flex items-center gap-1 text-[10px] text-dusty-700 hover:underline" data-testid={`sale-scheda-${s.id}-${idx}-${ci}`}>
                                <FileText className="h-3 w-3" /> {`${c.nome || ""} ${c.cognome || ""}`.trim() || "corsista"}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-4 text-slate-500">{FAD[r.fad] || "—"}</td>
                      <td className="py-2 px-4 text-right text-slate-500">{r.quantita || 1}</td>
                      {isOwner && <td className="px-4"></td>}
                      <td className="py-2 px-4 text-right text-slate-700">{fmtEur(r.imponibile != null ? r.imponibile : r.prezzo_vendita)}</td>
                      <td className="py-2 px-4 text-right text-slate-400">{fmtEur(r.iva_importo)}</td>
                      <td className="py-2 px-4 text-right text-amber-600">{fmtEur(r.royalty_importo)}</td>
                      <td className="py-2 px-4 text-right text-slate-500">{fmtEur(r.costo_riga != null ? r.costo_riga : (r.costo_servizio || 0) * (r.quantita || 1))}</td>
                      <td className="py-2 px-4 text-right text-emerald-600">{fmtEur(r.utile_netto)}</td>
                      <td className="px-4"></td><td className="px-4"></td><td className="px-4"></td><td className="px-4"></td>
                      {isOwner && <td className="px-4"></td>}
                    </tr>
                  ))}
                </Fragment>
              ))}
              {displaySales.length === 0 && (
                <tr><td colSpan={isOwner ? 18 : 16} className="py-12 text-center text-slate-400">{nameSearch ? "Nessuna vendita corrisponde alla ricerca" : "Nessuna vendita trovata"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isOwner && settle && (settle.saldo_partner || []).filter((x) => !filters.partner_id || x.partner_id === filters.partner_id).length > 0 && (
        <Card className="border-dusty-200 shadow-sm p-4" data-testid="settlement-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-800 flex items-center gap-2">
                Riepilogo periodo · conteggio con i partner
                {filters.saldato === "false" && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300" data-testid="settlement-da-saldare-badge">SOLO DA SALDARE</span>
                )}
              </h3>
              <p className="text-xs text-slate-500">
                {filters.dal || filters.al ? `Periodo: ${filters.dal || "inizio"} → ${filters.al || "oggi"}` : "Tutte le vendite (nessun periodo selezionato)"} · conteggio su vendite incassate/attive{filters.saldato === "false" ? " · escluse le fatture già saldate" : ""}.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {(settle.saldo_partner || []).filter((x) => !filters.partner_id || x.partner_id === filters.partner_id).map((x) => {
              const riv = x.tipo === "rivenditore";
              const dd = Number(x.da_dare || 0);
              const rivDovuto = Number(x.quota_ics || 0) + Number(x.quota_ics_sospeso || 0);
              const label = riv
                ? { txt: `${x.nome} deve a Safe / Associazione I.C.S.`, val: rivDovuto, cls: "text-teal-700", chip: "bg-teal-50 border-teal-200 text-teal-700" }
                : dd >= 0
                  ? { txt: `Safe deve a ${x.nome}`, val: dd, cls: "text-emerald-700", chip: "bg-emerald-50 border-emerald-200 text-emerald-700" }
                  : { txt: `${x.nome} deve a Safe`, val: -dd, cls: "text-rose-700", chip: "bg-rose-50 border-rose-200 text-rose-700" };
              return (
                <div key={x.partner_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 bg-white" data-testid={`settlement-row-${x.partner_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      {x.nome}
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${riv ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{riv ? "Rivenditore" : "Intermediario"}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {riv
                        ? `Dovuto a ICS (prezzo riservato + IVA)${Number(x.quota_ics_sospeso || 0) > 0 ? ` · di cui su vendite non ancora incassate: ${fmtEur(x.quota_ics_sospeso)}` : ""}`
                        : `Royalty maturata ${fmtEur(x.royalty || 0)} − incassato dal partner ${fmtEur(x.incassato_partner || 0)}`}
                      {!riv && Number(x.royalty_sospeso || 0) > 0 && ` · in sospeso (non ancora incassato): ${fmtEur(x.royalty_sospeso)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-sm font-bold px-2.5 py-1 rounded-lg border ${label.chip}`}>{label.txt}: {fmtEur(label.val)}</span>
                    <Button size="sm" variant="outline" onClick={() => saldaPeriodo(x.partner_id, x.nome)} data-testid={`salda-periodo-${x.partner_id}`}>Salda periodo</Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm border-t border-slate-100 pt-3">
            <span className="text-slate-600" data-testid="settlement-totale-da-ricevere">Totale Safe deve ai partner{filters.saldato === "false" ? " (da saldare)" : ""}: <b className="text-emerald-700">{fmtEur(settle.totale_da_ricevere || 0)}</b></span>
            <span className="text-slate-600" data-testid="settlement-totale-struttura">Totale dovuto alla struttura (rivenditori/partner){filters.saldato === "false" ? " (da saldare)" : ""}: <b className="text-teal-700">{fmtEur(settle.totale_deve_a_struttura || 0)}</b></span>
          </div>
        </Card>
      )}

      <Dialog open={storico !== null} onOpenChange={(v) => !v && setStorico(null)}>
        <DialogContent className="bg-white max-w-2xl" data-testid="storico-saldi-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Storico saldi periodici</DialogTitle>
            <DialogDescription>Registro delle chiusure "Salda periodo": partner, periodo, importo e data.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto -mx-2 px-2">
            {(!storico || storico.length === 0) && <div className="text-sm text-slate-400 text-center py-10">Nessuna chiusura registrata finora.</div>}
            <div className="space-y-2">
              {(storico || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`storico-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      {r.partner_nome}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${r.tipo === "rivenditore" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{r.tipo === "rivenditore" ? "Rivenditore" : "Intermediario"}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Periodo: {r.dal || "inizio"} → {r.al || "fine"} · {r.n_vendite} vendite · chiuso il {(r.created_at || "").slice(0, 10)}{r.created_by ? ` da ${r.created_by}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-bold text-dusty-700">{fmtEur(r.importo || 0)}</span>
                    <Button size="icon" variant="ghost" onClick={() => delStorico(r.id)} data-testid={`storico-del-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="sale-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica Vendita" : "Nuova Vendita"}</DialogTitle>
          <DialogDescription>Registra più corsi/servizi per lo stesso cliente. IVA, royalty e utile si calcolano automaticamente.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2 max-h-[68vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="sale-data" className="mt-1.5" /></div>
              <div><Label>Azienda cliente</Label>
                <Select value={form.azienda_id} onValueChange={(v) => setForm({ ...form, azienda_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-azienda"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Partner</Label>
                <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome} ({p.royalty_percent}%)</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>N. Fattura cliente</Label><Input value={form.numero_fattura} onChange={(e) => setForm({ ...form, numero_fattura: e.target.value })} data-testid="sale-numero-fattura" placeholder="es. 2026/000123" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N. Ordine</Label><Input value={form.numero_ordine} onChange={(e) => setForm({ ...form, numero_ordine: e.target.value })} data-testid="sale-numero-ordine" placeholder="es. #1200" className="mt-1.5" /></div>
              <div><Label>Modalità di incasso</Label>
                <Select value={form.modalita_incasso} onValueChange={(v) => setForm({ ...form, modalita_incasso: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-modalita"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="carta">Carta</SelectItem>
                    <SelectItem value="omaggio">Omaggio</SelectItem>
                    <SelectItem value="fondi">Fondi</SelectItem>
                    <SelectItem value="abbonamento">Abbonamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stato pagamento</Label>
                <Select value={form.stato_pagamento} onValueChange={(v) => setForm({ ...form, stato_pagamento: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pagato">Pagato</SelectItem>
                    <SelectItem value="non_pagato">Non Pagato</SelectItem>
                    <SelectItem value="in_sospeso">In Sospeso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Stato evasione</Label>
                <Select value={form.stato_evasione} onValueChange={(v) => setForm({ ...form, stato_evasione: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-evasione"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evaso">Evaso</SelectItem>
                    <SelectItem value="in_attesa">In attesa di evasione</SelectItem>
                    <SelectItem value="trasmesso">Trasmesso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Incassato da</Label>
              <Select value={form.incassato_da} onValueChange={(v) => setForm({ ...form, incassato_da: v })}>
                <SelectTrigger className="mt-1.5" data-testid="sale-incassato-da"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="safe">Safe (incassa la Safe)</SelectItem>
                  <SelectItem value="partner">Partner (ha incassato il partner)</SelectItem>
                  <SelectItem value="ics">Associazione I.C.S.</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-700 font-semibold">Corsi / Servizi</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi corso</Button>
              </div>

              <div className="rounded-lg border border-dusty-200 bg-dusty-50/50 p-3 mb-3" data-testid="quick-add-panel">
                <div className="flex items-center gap-1.5 mb-2 text-dusty-800 font-semibold text-sm">
                  <Zap className="h-4 w-4" /> Aggiunta rapida: un corsista su più corsi
                </div>
                <p className="text-[11px] text-slate-500 mb-2">Inserisci il corsista una sola volta, spunta tutti i corsi che deve frequentare e aggiungili in un colpo solo.</p>
                <div className="rounded-md border border-slate-200 bg-white p-2 mb-2">
                  <CorsistaFields value={quick.corsista} onChange={(next) => setQuick((q) => ({ ...q, corsista: next }))} idPrefix="quick-corsista" />
                </div>
                <div className="flex flex-wrap gap-1.5 items-center mb-2" data-testid="quick-packages-row">
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Boxes className="h-3.5 w-3.5" /> Pacchetti:</span>
                  {packages.map((p) => (
                    <button key={p.id} type="button" onClick={() => applyPackage(p)} data-testid={`quick-package-${p.id}`}
                      className="text-xs font-medium px-2.5 py-1 rounded-full border border-dusty-300 bg-white text-dusty-700 hover:bg-dusty-100 transition-colors">
                      {p.nome} <span className="opacity-60">({(p.servizio_ids || []).length})</span>
                    </button>
                  ))}
                  {packages.length === 0 && <span className="text-xs text-slate-400">nessun pacchetto</span>}
                  <button type="button" onClick={openPkgManage} data-testid="manage-packages-btn"
                    className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Settings2 className="h-3.5 w-3.5" /> Gestisci pacchetti
                  </button>
                </div>
                <div className="mb-2">
                  <Input value={quick.search} onChange={(e) => setQuick((q) => ({ ...q, search: e.target.value }))} placeholder="Cerca un corso da spuntare…" className="h-9 bg-white" data-testid="quick-search" />
                </div>
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="quick-course-list">
                  {services
                    .filter((s) => !quick.search || s.nome.toLowerCase().includes(quick.search.toLowerCase()))
                    .map((s) => {
                      const checked = quick.servizi.includes(s.id);
                      const prezzo = priceForSale(s);
                      return (
                        <label key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 text-sm" data-testid={`quick-course-${s.id}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleQuickServizio(s.id)} data-testid={`quick-course-check-${s.id}`} />
                          <span className="flex-1 text-slate-700">{s.nome}</span>
                          <span className="text-xs text-slate-400">{fmtEur(prezzo)}</span>
                        </label>
                      );
                    })}
                  {services.filter((s) => !quick.search || s.nome.toLowerCase().includes(quick.search.toLowerCase())).length === 0 && (
                    <div className="px-2.5 py-3 text-xs text-slate-400 text-center">Nessun corso trovato</div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-500">{quick.servizi.length} cors{quick.servizi.length === 1 ? "o" : "i"} selezionat{quick.servizi.length === 1 ? "o" : "i"}</span>
                  <Button type="button" size="sm" onClick={quickAdd} disabled={quick.servizi.length === 0} className="bg-dusty-600 hover:bg-dusty-700 text-white h-8" data-testid="quick-add-btn">
                    <Check className="h-4 w-4 mr-1" /> Aggiungi ai corsi selezionati
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {form.righe.map((r, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 relative" data-testid={`sale-line-${i}`}>
                    {form.righe.length > 1 && (
                      <button type="button" onClick={() => removeLine(i)} data-testid={`remove-line-${i}`}
                        className="absolute top-2 right-2 text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                    )}
                    <div className="mb-2 pr-6">
                      <Label className="text-xs text-slate-600 font-medium">Servizio/Corso</Label>
                      <Select value={r.servizio_id} onValueChange={(v) => onLineService(i, v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`line-servizio-${i}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs text-slate-600 font-medium">Prezzo unit. (€)</Label><Input type="number" step="0.01" value={r.prezzo_vendita} onChange={(e) => setLine(i, { prezzo_vendita: e.target.value })} data-testid={`line-prezzo-${i}`} className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-600 font-medium">Costo unit. (€)</Label><Input type="number" step="0.01" value={r.costo_servizio} onChange={(e) => setLine(i, { costo_servizio: e.target.value })} data-testid={`line-costo-${i}`} className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-600 font-medium">Quantità</Label><Input type="number" step="1" min="1" value={r.quantita} onChange={(e) => setLine(i, { quantita: e.target.value })} data-testid={`line-quantita-${i}`} className="mt-1 h-9" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div><Label className="text-xs text-slate-600 font-medium">IVA</Label>
                        <Select value={String(r.iva_rate)} onValueChange={(v) => setLine(i, { iva_rate: Number(v) })}>
                          <SelectTrigger className="mt-1 h-9" data-testid={`line-iva-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="0.22">IVA 22%</SelectItem><SelectItem value="0">Esente</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs text-slate-600 font-medium">FAD</Label>
                        <Select value={r.fad} onValueChange={(v) => setLine(i, { fad: v })}>
                          <SelectTrigger className="mt-1 h-9" data-testid={`line-fad-${i}`}><SelectValue placeholder="Nessuna" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuna</SelectItem>
                            <SelectItem value="lcr">LCR</SelectItem>
                            <SelectItem value="kattedra">Kattedra</SelectItem>
                            <SelectItem value="safe">Safe</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {r.fad && r.fad !== "none" && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div><Label className="text-xs text-slate-600 font-medium">Costo piattaforma (€)</Label>
                          <Input type="number" step="0.01" value={r.costo_piattaforma} onChange={(e) => setLine(i, { costo_piattaforma: e.target.value })} data-testid={`line-costo-piattaforma-${i}`} className="mt-1 h-9" placeholder="0.00" />
                          <p className="text-[11px] text-slate-400 mt-0.5">Aggiunto al prezzo della FAD ({FAD[r.fad]}).</p>
                        </div>
                      </div>
                    )}
                    <div className="mt-2">
                      <Label className="text-xs text-slate-600 font-medium">Stato di avanzamento</Label>
                      <Select value={r.stato_avanzamento} onValueChange={(v) => setLine(i, { stato_avanzamento: v })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`line-avanzamento-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_completamento">In completamento</SelectItem>
                          <SelectItem value="attivato">Attivato</SelectItem>
                          <SelectItem value="trasmesso">Trasmesso</SelectItem>
                          <SelectItem value="completato">Completato</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-600">Corsisti (facoltativo)</span>
                        <button type="button" onClick={() => addCorsista(i)} className="text-xs text-dusty-800 hover:text-dusty-900 font-semibold inline-flex items-center gap-1" data-testid={`line-add-corsista-${i}`}><UserPlus className="h-3.5 w-3.5" /> Aggiungi corsista</button>
                      </div>
                      <div className="space-y-2">
                        {(r.corsisti || []).map((c, ci) => (
                          <div key={ci} className="rounded-md border border-slate-200 bg-white p-2 relative" data-testid={`line-corsista-${i}-${ci}`}>
                            <button type="button" onClick={() => removeCorsista(i, ci)} className="absolute top-1.5 right-1.5 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                            <CorsistaFields value={c} onChange={(next) => setCorsista(i, ci, next)} idPrefix={`line-corsista-${i}-${ci}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="sale-note" placeholder="Annotazioni sulla vendita…" className="mt-1.5" />
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2" data-testid="sale-preview">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Imponibile ({form.righe.length} righe)</span><span className="font-medium text-slate-700">{fmtEur(totals.prezzo)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">IVA calcolata</span><span className="font-medium text-slate-700">{fmtEur(totals.iva)}</span></div>
              {isRivenditore ? (
                <>
                  <div className="flex justify-between text-sm"><span className="text-violet-600">ICS fattura al rivenditore ({rivPct}% + IVA)</span><span className="font-medium text-violet-700" data-testid="preview-rivendita">{fmtEur(totals.rivendita)}</span></div>
                  <div className="text-[11px] text-slate-400">Partner rivenditore: nessuna royalty; l'importo sopra è quanto ICS gli fattura.</div>
                </>
              ) : (
                <div className="flex justify-between text-sm"><span className="text-slate-500">Royalty partner ({royaltyPct}% sull'utile)</span><span className="font-medium text-amber-700">{fmtEur(totals.royalty)}</span></div>
              )}
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2"><span className="font-semibold text-slate-700">Utile netto totale</span><span className="font-bold text-emerald-700" data-testid="preview-utile">{fmtEur(totals.utile)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="sale-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="packages-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Boxes className="h-5 w-5 text-dusty-600" /> Gestisci pacchetti di corsi</DialogTitle>
            <DialogDescription>Crea gruppi di corsi venduti spesso insieme, per spuntarli in un clic nell'aggiunta rapida.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {packages.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Pacchetti esistenti</Label>
                {packages.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`pkg-row-${p.id}`}>
                    <span className="text-slate-700"><span className="font-medium">{p.nome}</span> <span className="text-slate-400">· {(p.servizio_ids || []).length} corsi</span></span>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editPkg(p)} data-testid={`pkg-edit-${p.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delPkg(p.id)} data-testid={`pkg-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-dusty-200 bg-dusty-50/50 p-3 space-y-2">
              <Label className="text-dusty-800 font-semibold text-sm">{pkgEditId ? "Modifica pacchetto" : "Nuovo pacchetto"}</Label>
              <Input value={pkgForm.nome} onChange={(e) => setPkgForm({ ...pkgForm, nome: e.target.value })} placeholder="Nome pacchetto (es. Sicurezza base)" className="bg-white h-9" data-testid="pkg-nome" />
              <Input value={pkgForm.search} onChange={(e) => setPkgForm({ ...pkgForm, search: e.target.value })} placeholder="Cerca un corso…" className="bg-white h-9" data-testid="pkg-search" />
              <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="pkg-course-list">
                {services.filter((s) => !pkgForm.search || s.nome.toLowerCase().includes(pkgForm.search.toLowerCase())).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 text-sm" data-testid={`pkg-course-${s.id}`}>
                    <Checkbox checked={pkgForm.servizio_ids.includes(s.id)} onCheckedChange={() => togglePkgServizio(s.id)} data-testid={`pkg-course-check-${s.id}`} />
                    <span className="flex-1 text-slate-700">{s.nome}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">{pkgForm.servizio_ids.length} corsi nel pacchetto</span>
                <div className="flex gap-2">
                  {pkgEditId && <Button size="sm" variant="outline" className="h-8" onClick={() => { setPkgForm({ nome: "", servizio_ids: [], search: "" }); setPkgEditId(null); }}>Annulla modifica</Button>}
                  <Button size="sm" onClick={savePkg} className="bg-dusty-600 hover:bg-dusty-700 text-white h-8" data-testid="pkg-save">{pkgEditId ? "Aggiorna" : "Crea pacchetto"}</Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
